import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Checkbox, useToastStore } from '@/shared/ui';

import type { ImportRunOptions } from '../model/types';
import { useSetImportOptions } from '../queries/useImport';
import styles from './ImportOptions.module.css';

/**
 * Znaczniki dopuszczone w wyjaśnieniu.
 *
 * ⚠ **Każdy użyty znacznik MUSI tu być**, inaczej `Trans` wypisze go dosłownie jako tekst —
 * i to jest cicha usterka: test na fragment zdania przechodzi, bo szuka treści MIĘDZY
 * znacznikami. Złapane dopiero okiem w przeglądarce (2026-09-04).
 */
const EXPLANATION_TAGS = { p: <p />, b: <b /> };

interface Props {
  runId: number;
  options: ImportRunOptions;
  /** Przebieg zamknięty — opcje są wtedy zapisem tego, co się stało, a nie wyborem. */
  readOnly: boolean;
}

/**
 * Sekcja opcji nad importem (słowo usera 2026-09-04).
 *
 * ⭐ **Opcje stoją PRZY PRZEBIEGU i są edytowalne do zatwierdzenia**, a nie ustawiane przy
 * wgrywaniu pliku: przełącznik zmienia liczby, na które człowiek właśnie patrzy. Decyzja
 * podjęta przed wgraniem byłaby decyzją podjętą, zanim zobaczył cokolwiek.
 *
 * ⚠ Wyjaśnienie idzie w **popoverze**, nie w natywnym tooltipie: ma być obszerne, a natywny
 * nie działa na dotyku, znika przy próbie przeczytania i nie mieści akapitu.
 */
export function ImportOptions({ runId, options, readOnly }: Props) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const zapisz = useSetImportOptions(runId);

  function ustaw(zmiana: Partial<ImportRunOptions>) {
    zapisz.mutate(
      { ...options, ...zmiana },
      { onError: (error) => show({ message: describeError(error), tone: 'danger' }) },
    );
  }

  return (
    <section className={styles['card']} aria-labelledby={`import-options-${runId}`}>
      <header className={styles['head']}>
        <span className={styles['icon']} aria-hidden="true">
          <SlidersHorizontal size={16} />
        </span>
        <h2 className={styles['title']} id={`import-options-${runId}`}>
          {t('import.options.title')}
        </h2>
      </header>

      <div className={styles['body']}>
        <Checkbox
          checked={options.overwriteDisplayNames}
          disabled={readOnly || zapisz.isPending}
          onCheckedChange={(value) => ustaw({ overwriteDisplayNames: value })}
          label={t('import.options.overwriteNames.label')}
          hint={t('import.options.overwriteNames.hint')}
          explanationLabel={t('import.options.explain', {
            option: t('import.options.overwriteNames.label'),
          })}
          explanation={
            <Trans
              i18nKey="import.options.overwriteNames.explanation"
              components={EXPLANATION_TAGS}
            />
          }
        />

        <Checkbox
          checked={options.assignInactiveOwners}
          disabled={readOnly || zapisz.isPending}
          onCheckedChange={(value) => ustaw({ assignInactiveOwners: value })}
          label={t('import.options.assignInactive.label')}
          hint={t('import.options.assignInactive.hint')}
          explanationLabel={t('import.options.explain', {
            option: t('import.options.assignInactive.label'),
          })}
          explanation={
            <Trans
              i18nKey="import.options.assignInactive.explanation"
              components={EXPLANATION_TAGS}
            />
          }
        />
      </div>
    </section>
  );
}
